import React, { useState, useEffect } from 'react';
import { FileText, Plus, Trash2, Edit2, Save, X, Loader2, AlertCircle, Check, GripVertical, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminProfileFields = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newField, setNewField] = useState({
    field_id: '',
    label: '',
    field_type: 'text',
    options: [],
    required: false,
    visible: true,
    order: 0,
    section: 'basic'
  });

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'number', label: 'Number' },
    { value: 'select', label: 'Dropdown' },
    { value: 'multiselect', label: 'Multi-Select' },
    { value: 'date', label: 'Date' }
  ];

  const sections = [
    { value: 'basic', label: 'Basic Info' },
    { value: 'personal', label: 'Personal Details' },
    { value: 'family', label: 'Family Background' },
    { value: 'preferences', label: 'Partner Preferences' }
  ];

  const fetchFields = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/phase2/profile-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFields(response.data.fields || []);
    } catch (err) {
      console.error('Error fetching fields:', err);
      setError('Failed to load profile fields');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, []);

  const handleCreateField = async () => {
    if (!newField.field_id || !newField.label) {
      setError('Field ID and Label are required');
      return;
    }

    const token = localStorage.getItem('token');
    setSaving('new');
    setError(null);

    try {
      const fieldData = {
        ...newField,
        field_id: newField.field_id.toLowerCase().replace(/\s+/g, '_'),
        order: fields.length + 1
      };

      await axios.post(`${API_URL}/api/admin/phase2/profile-fields`, fieldData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Field created successfully');
      setTimeout(() => setSuccess(null), 3000);
      setShowAddModal(false);
      setNewField({
        field_id: '',
        label: '',
        field_type: 'text',
        options: [],
        required: false,
        visible: true,
        order: 0,
        section: 'basic'
      });
      fetchFields();
    } catch (err) {
      console.error('Error creating field:', err);
      setError(err.response?.data?.detail || 'Failed to create field');
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateField = async (field) => {
    const token = localStorage.getItem('token');
    setSaving(field.field_id);
    setError(null);

    try {
      await axios.put(`${API_URL}/api/admin/phase2/profile-fields/${field.field_id}`, field, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Field updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      setEditingField(null);
      fetchFields();
    } catch (err) {
      console.error('Error updating field:', err);
      setError(err.response?.data?.detail || 'Failed to update field');
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this field?')) return;

    const token = localStorage.getItem('token');
    setSaving(fieldId);
    setError(null);

    try {
      await axios.delete(`${API_URL}/api/admin/phase2/profile-fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Field deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchFields();
    } catch (err) {
      console.error('Error deleting field:', err);
      setError(err.response?.data?.detail || 'Failed to delete field');
    } finally {
      setSaving(null);
    }
  };

  const toggleVisibility = async (field) => {
    await handleUpdateField({ ...field, visible: !field.visible });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  const groupedFields = sections.map(section => ({
    ...section,
    fields: fields.filter(f => f.section === section.value)
  }));

  return (
    <div className="space-y-6" data-testid="admin-profile-fields">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile Fields</h1>
          <p className="text-gray-600 mt-1">Manage user profile form fields and options</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors"
          data-testid="add-field-btn"
        >
          <Plus className="w-4 h-4" />
          Add Field
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-500" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Field Groups */}
      {groupedFields.map(group => (
        <div key={group.value} className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">{group.label}</h3>
            <p className="text-sm text-gray-500">{group.fields.length} fields</p>
          </div>
          
          {group.fields.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No fields in this section
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {group.fields.sort((a, b) => a.order - b.order).map(field => (
                <div
                  key={field.field_id}
                  className="p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                  data-testid={`field-${field.field_id}`}
                >
                  <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{field.label}</span>
                      {field.required && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded">Required</span>
                      )}
                      {!field.visible && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">Hidden</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span>ID: {field.field_id}</span>
                      <span>•</span>
                      <span>Type: {field.field_type}</span>
                      {field.options?.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{field.options.length} options</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleVisibility(field)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title={field.visible ? 'Hide field' : 'Show field'}
                    >
                      {field.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditingField(field)}
                      className="p-2 text-gray-400 hover:text-mint-600 hover:bg-mint-50 rounded-lg transition-colors"
                      title="Edit field"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteField(field.field_id)}
                      disabled={['name', 'age', 'gender', 'email'].includes(field.field_id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Delete field"
                    >
                      {saving === field.field_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Add/Edit Modal */}
      {(showAddModal || editingField) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingField ? 'Edit Field' : 'Add New Field'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingField(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {!editingField && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field ID</label>
                  <input
                    type="text"
                    value={newField.field_id}
                    onChange={(e) => setNewField(prev => ({ ...prev, field_id: e.target.value }))}
                    placeholder="e.g., hobbies"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Unique identifier (lowercase, no spaces)</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={editingField?.label || newField.label}
                  onChange={(e) => editingField 
                    ? setEditingField(prev => ({ ...prev, label: e.target.value }))
                    : setNewField(prev => ({ ...prev, label: e.target.value }))
                  }
                  placeholder="e.g., Hobbies & Interests"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                  <select
                    value={editingField?.field_type || newField.field_type}
                    onChange={(e) => editingField
                      ? setEditingField(prev => ({ ...prev, field_type: e.target.value }))
                      : setNewField(prev => ({ ...prev, field_type: e.target.value }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  >
                    {fieldTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                  <select
                    value={editingField?.section || newField.section}
                    onChange={(e) => editingField
                      ? setEditingField(prev => ({ ...prev, section: e.target.value }))
                      : setNewField(prev => ({ ...prev, section: e.target.value }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  >
                    {sections.map(section => (
                      <option key={section.value} value={section.value}>{section.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {['select', 'multiselect'].includes(editingField?.field_type || newField.field_type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                  <textarea
                    value={(editingField?.options || newField.options).join('\n')}
                    onChange={(e) => {
                      const options = e.target.value.split('\n').filter(o => o.trim());
                      editingField
                        ? setEditingField(prev => ({ ...prev, options }))
                        : setNewField(prev => ({ ...prev, options }));
                    }}
                    placeholder="Enter each option on a new line"
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  />
                </div>
              )}

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingField?.required || newField.required}
                    onChange={(e) => editingField
                      ? setEditingField(prev => ({ ...prev, required: e.target.checked }))
                      : setNewField(prev => ({ ...prev, required: e.target.checked }))
                    }
                    className="w-4 h-4 rounded border-gray-300 text-mint-500 focus:ring-mint-500"
                  />
                  <span className="text-sm text-gray-700">Required field</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingField?.visible ?? newField.visible}
                    onChange={(e) => editingField
                      ? setEditingField(prev => ({ ...prev, visible: e.target.checked }))
                      : setNewField(prev => ({ ...prev, visible: e.target.checked }))
                    }
                    className="w-4 h-4 rounded border-gray-300 text-mint-500 focus:ring-mint-500"
                  />
                  <span className="text-sm text-gray-700">Visible to users</span>
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingField(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => editingField ? handleUpdateField(editingField) : handleCreateField()}
                disabled={saving === 'new' || saving === editingField?.field_id}
                className="flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 disabled:opacity-50 transition-colors"
              >
                {(saving === 'new' || saving === editingField?.field_id) ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {editingField ? 'Update' : 'Create'} Field
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfileFields;
